import { createSlice } from '@reduxjs/toolkit';
import { without } from 'lodash';
import { ACTION_TYPES as TASKS_ACTION_TYPES } from '../tasks/constants';
import { ACTION_TYPES as PROJECTS_ACTION_TYPES } from '../projects/constants';
import { ACTION_TYPES } from './constants';

const addTaskToSection = (state, action) => {
  const { taskId, task } = action.payload || {};
  const sectionId = task?.sectionId;
  if (!sectionId) return;

  if (!state[sectionId]) state[sectionId] = {};
  const section = state[sectionId];
  if (section.tasks?.includes(taskId)) return;

  section.tasks = [taskId, ...(section.tasks || [])];
  section.sectionCounts = (section.sectionCounts || 0) + 1;
};

const removeTaskFromSection = (state, action) => {
  const { taskId, sectionId, isCompleted } = action.payload || {};
  const section = state[sectionId];
  if (!section) return;

  const hasTask = section.tasks?.includes(taskId);
  if (!hasTask) return;

  section.tasks = without(section.tasks, taskId);
  section.sectionCounts = Math.max((section.sectionCounts || 0) - 1, 0);
  if (isCompleted) {
    section.sectionDoneCounts = Math.max(
      (section.sectionDoneCounts || 0) - 1,
      0,
    );
  }
};

const sectionsSlice = createSlice({
  name: 'sections',
  initialState: {},
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(
        TASKS_ACTION_TYPES.GET_TASKS_REQUEST_SUCCESS,
        (state, action) => {
          Object.entries(action.payload?.sections || {}).forEach(
            ([sectionId, sectionData]) => {
              state[sectionId] = {
                ...(state[sectionId] || {}),
                ...sectionData,
              };
            },
          );
        },
      )
      .addCase(
        TASKS_ACTION_TYPES.GET_TASKS_INFINITE_LIST_NEXT_PAGE_SUCCESS,
        (state, action) => {
          Object.entries(action.payload?.sections || {}).forEach(
            ([sectionId, newTaskIds]) => {
              if (!state[sectionId]) state[sectionId] = {};
              state[sectionId].tasks = Array.from(
                new Set([
                  ...(state[sectionId].tasks || []),
                  ...(newTaskIds || []),
                ]),
              );
            },
          );
        },
      )
      .addCase(
        TASKS_ACTION_TYPES.GET_SECTION_TASKS_REQUEST_SUCCESS,
        (state, action) => {
          const { sectionId, appendedTaskIdList = [] } = action.payload || {};
          if (!state[sectionId]) state[sectionId] = {};
          state[sectionId].tasks = Array.from(
            new Set([...(state[sectionId].tasks || []), ...appendedTaskIdList]),
          );
        },
      )
      .addCase(
        TASKS_ACTION_TYPES.GET_TASK_BY_IDENTIFIER_REQUEST_SUCCESS,
        (state, action) => {
          Object.values(action.payload?.sectionsInfo || {}).forEach(
            (section) => {
              state[section._id] = {
                ...(state[section._id] || {}),
                ...section,
              };
            },
          );
        },
      )
      .addCase(TASKS_ACTION_TYPES.SET_PROJECT_TASKS_FILTERS, (state) => {
        Object.keys(state).forEach((sectionId) => {
          state[sectionId].page = 1;
        });
      })
      .addCase(
        ACTION_TYPES.GET_SECTIONS_COUNT_REQUEST_SUCCESS,
        (state, action) => {
          Object.entries(action.payload || {}).forEach(([sectionId, count]) => {
            if (!state[sectionId]) state[sectionId] = {};
            state[sectionId].sectionCounts = count;
          });
        },
      )
      .addCase(ACTION_TYPES.INITIALIZE_SECTION_QUERY_STATE, (state, action) => {
        Object.values(action.payload || {}).forEach((section) => {
          // eslint-disable-next-line no-unused-vars
          const { tasks, ...sectionWithoutTasks } = section || {};
          state[section._id] = {
            ...(state[section._id] || {}),
            ...sectionWithoutTasks,
            page: 1,
          };
        });
      })
      .addCase(ACTION_TYPES.UPDATE_SECTION_QUERY_STATE, (state, action) => {
        const sectionId = action.payload;
        if (!state[sectionId]) state[sectionId] = {};
        state[sectionId].page = (state[sectionId].page || 1) + 1;
      })
      .addCase(
        PROJECTS_ACTION_TYPES.GET_PROJECT_BY_ID_BASIC_FIELDS_REQUEST_SUCCESS,
        (state, action) => {
          Object.entries(action.payload?.sections || {}).forEach(
            ([sectionId, sectionData]) => {
              // eslint-disable-next-line no-unused-vars
              const { tasks, ...sectionWithoutTasks } = sectionData || {};
              state[sectionId] = {
                ...(state[sectionId] || {}),
                ...sectionWithoutTasks,
              };
            },
          );
        },
      )
      .addCase(ACTION_TYPES.ADD_SECTION_REQUEST_SUCCESS, (state, action) => {
        state[action.payload._id] = action.payload;
      })
      .addCase(TASKS_ACTION_TYPES.ADD_TASK_SUCCESS, addTaskToSection)
      .addCase(TASKS_ACTION_TYPES.ADD_TASK_EVENT, addTaskToSection)
      .addCase(TASKS_ACTION_TYPES.DELETE_TASK_REQUEST, removeTaskFromSection)
      .addCase(TASKS_ACTION_TYPES.REMOVE_TASK_EVENT, removeTaskFromSection)
      .addCase(TASKS_ACTION_TYPES.TASKS_DRAG_N_DROP_ACTION, (state, action) => {
        const { fromSection, toSection, isSameSection, isCompleted, taskId } =
          action.payload || {};
        if (isSameSection) return;

        const from = state[fromSection];
        if (from) {
          const hasTask = from.tasks?.includes(taskId);
          if (hasTask) {
            from.tasks = without(from.tasks, taskId);
            from.sectionCounts = Math.max((from.sectionCounts || 0) - 1, 0);
            if (isCompleted) {
              from.sectionDoneCounts = Math.max(
                (from.sectionDoneCounts || 0) - 1,
                0,
              );
            }
          }
        }

        if (!state[toSection]) state[toSection] = {};
        const to = state[toSection];
        const hasTaskInTo = to.tasks?.includes(taskId);
        if (!hasTaskInTo) {
          to.tasks = [...(to.tasks || []), taskId];
          to.sectionCounts = (to.sectionCounts || 0) + 1;
          if (isCompleted) {
            to.sectionDoneCounts = (to.sectionDoneCounts || 0) + 1;
          }
        }
      })
      .addCase(
        TASKS_ACTION_TYPES.REMOVE_PROJECT_FROM_TASK_SUCCESS,
        (state, action) => {
          const { taskId, sectionId } = action.payload || {};
          if (!state[sectionId]) return;
          state[sectionId].tasks = without(
            state[sectionId].tasks || [],
            taskId,
          );
          state[sectionId].sectionCounts = Math.max(
            (state[sectionId].sectionCounts || 0) - 1,
            0,
          );
        },
      )
      .addCase(
        TASKS_ACTION_TYPES.ADD_TASK_TO_PROJECT_REQUEST_SUCCESS,
        (state, action) => {
          const { taskId, sectionId } = action.payload || {};
          if (!state[sectionId]) state[sectionId] = {};
          state[sectionId].tasks = [...(state[sectionId].tasks || []), taskId];
          state[sectionId].sectionCounts =
            (state[sectionId].sectionCounts || 0) + 1;
        },
      )
      .addCase(TASKS_ACTION_TYPES.UPDATE_TASK_EVENT, (state, action) => {
        const {
          taskId,
          task: { projectAndSectionLabelList } = {},
          prevSections,
        } = action.payload || {};

        const newSectionIds = [];

        Object.values(projectAndSectionLabelList || {}).forEach(
          ({ sectionId, isOutdated }) => {
            if (isOutdated) return;
            newSectionIds.push(sectionId);

            if (!state[sectionId]) state[sectionId] = {};
            const section = state[sectionId];
            if (!section.tasks?.includes(taskId)) {
              section.tasks = [...(section.tasks || []), taskId];
              section.sectionCounts = (section.sectionCounts || 0) + 1;
            }
          },
        );

        (prevSections || []).forEach((prevSectionId) => {
          if (newSectionIds.includes(prevSectionId)) return;
          const section = state[prevSectionId];
          if (!section) return;
          if (section.tasks?.includes(taskId)) {
            section.tasks = without(section.tasks, taskId);
            section.sectionCounts = Math.max(
              (section.sectionCounts || 0) - 1,
              0,
            );
          }
        });
      });
  },
});

export default sectionsSlice.reducer;
